import styled from 'styled-components';

import { AssignList } from '@components/assign-button';
import { IconAssign } from '@components/icons/assign';
import { IconPerson } from '@components/icons/person';
import { StatusIndicator, StatusIndicatorProps } from '@components/status-indicator';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';

import type { JourneyNode } from './types';

const AssigneesContainer = styled.div({ display: 'flex' });

const MAX_VISIBLE_ASSIGNEES = 3;
const MAX_VISIBLE_ASSIGNEES_WITH_OVERFLOW = MAX_VISIBLE_ASSIGNEES - 1;

type AssigneesProps = Partial<StatusIndicatorProps> & {
  assignees: JourneyNode['data']['assignees'];
};

export const Assignees = ({
  assignees = [],
  variant = 'notStarted',
  ratio,
  icon = <IconPerson />,
  label,
  ...rest
}: AssigneesProps) => {
  const { t } = useScopedTranslate(locale);
  const hasOverflow = assignees.length > MAX_VISIBLE_ASSIGNEES;
  const visibleCount = hasOverflow ? MAX_VISIBLE_ASSIGNEES_WITH_OVERFLOW : MAX_VISIBLE_ASSIGNEES;
  const hiddenAssignees = assignees.slice(visibleCount);

  return (
    <AssigneesContainer {...rest}>
      <AssignList>
        {assignees.length === 0 ? (
          <AssignList.Icon icon={<IconAssign />} tooltip={t('unassigned')} />
        ) : (
          <>
            {hasOverflow && (
              <Tooltip content={hiddenAssignees.map((user) => user.name).join('\n')}>
                <AssignList.Avatar
                  assignee={{ id: 'overflow', name: `+${hiddenAssignees.length}`, picture: '' }}
                  noAbbreviate
                  color="theme"
                  label={hiddenAssignees.map((user) => user.name).join(', ')}
                />
              </Tooltip>
            )}
            {assignees.slice(0, visibleCount).map((user) => (
              <Tooltip key={user.id} content={user.name}>
                <AssignList.Avatar assignee={user} />
              </Tooltip>
            ))}
          </>
        )}
        <StatusIndicator variant={variant} icon={icon} ratio={ratio} label={label} />
      </AssignList>
    </AssigneesContainer>
  );
};

const locale = {
  en: {
    unassigned: 'Unassigned',
  },
};
